import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/hooks/use-locale";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { trackCTAClick } from "@/lib/analytics";

const AnimatedSection = ({ children, className = "", style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) => {
  const [ref, isVisible] = useScrollAnimation<HTMLElement>();
  return (
    <section
      ref={ref}
      style={style}
      className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
    >
      {children}
    </section>
  );
};

const AboutUs = () => {
  const { t } = useTranslation();
  const { prefix } = useLocale();

  const principles = [
    { title: t("aboutUs.principle1Title"), desc: t("aboutUs.principle1Desc") },
    { title: t("aboutUs.principle2Title"), desc: t("aboutUs.principle2Desc") },
    { title: t("aboutUs.principle3Title"), desc: t("aboutUs.principle3Desc") },
    { title: t("aboutUs.principle4Title"), desc: t("aboutUs.principle4Desc") },
  ];

  const faqs = [
    { q: "What is Queebi Land?", a: "Queebi Land is a character-driven emotional learning universe designed for children ages 3–8. Through six lovable animal friends, children explore emotions like courage, curiosity, kindness, patience, empathy, and self-confidence in a gentle, play-based way." },
    { q: "What age is Queebi designed for?", a: "Queebi is designed primarily for children ages 3–8, though younger toddlers can also enjoy the products (PaciBuddies, comforters, swaddles) from birth. The activities and stories are most suitable for preschool and early elementary ages." },
    { q: "How can Queebi help my child?", a: "Queebi helps children name and understand their emotions through relatable characters and simple activities. Whether your child is navigating separation anxiety, learning to share, or building confidence, Queebi provides age-appropriate tools to support their emotional growth." },
    { q: "Where can I buy Queebi products?", a: "Queebi products are available on Amazon. Visit our Products page to browse Gift Sets, Comforters, PaciBuddies, Swaddles, and Storage Boxes, each featuring the beloved Queebi characters." },
    { q: "Are Queebi products safe for newborns?", a: "Yes. All Queebi products are made with BPA-free, baby-safe materials and meet international safety standards. PaciBuddies feature detachable pacifier clips for safe use, and our fabrics are soft, hypoallergenic, and machine washable." },
    { q: "Can Queebi be used in classrooms?", a: "Absolutely! Queebi's emotion cards, coloring pages, and character stories are designed to integrate seamlessly into preschool and kindergarten social-emotional learning (SEL) curricula. Teachers can download free printable resources from our Activities page." },
    { q: "What makes Queebi different from other children's character brands?", a: "Queebi was created with emotional development at its core — not as an afterthought. Each of the six characters represents a specific emotional quality (courage, curiosity, kindness, patience, empathy, self-confidence), and every product, story, and activity is designed to help children recognize and express their feelings in a meaningful way." },
    { q: "Can I license Queebi characters for my products?", a: "Yes! Queebi Land is an open IP designed for thoughtful partnerships. Whether you're a publisher, educator, brand, or product creator, we offer flexible licensing for books, toys, educational materials, home décor, and more. Visit our Collaborate page or email hello@queebi.com.tw." },
    { q: "How do I collaborate with Queebi?", a: "We welcome partnerships in licensing, co-creation, brand integration, and educational collaboration. Visit our Collaborate page or email hello@queebi.com.tw to start a conversation." },
  ];

  const purposeItems = [
    { title: t("aboutUs.explore"), desc: t("aboutUs.exploreDesc") },
    { title: t("aboutUs.nurture"), desc: t("aboutUs.nurtureDesc") },
    { title: t("aboutUs.growConfidence"), desc: t("aboutUs.growConfidenceDesc") },
  ];

  return (
    <>
      <PageHead
        title={t("aboutUs.seoTitle")}
        description={t("aboutUs.seoDesc")}
        canonical="https://queebi.com/about-us"
        image="/images/og/about-1200x630.jpg"
        imageAlt="About BabyLux & Queebi Land — a family-owned children's IP brand"
        keywords="Queebi about, emotional learning, children emotional development, play-based learning, early childhood education, social emotional curriculum, SEL preschool, IP licensing children, baby product brand"
      />

      <section className="relative">
        <img src="/images/pages/about-hero.png" alt="About Queebi Land" className="w-full h-80 md:h-[500px] object-cover object-top" width={1400} height={500} loading="eager" />
      </section>

      <AnimatedSection className="bg-background py-20 md:py-32">
        <div className="container max-w-5xl">
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-center mb-4">{t("aboutUs.purposeTitle")}</h1>
          <h2 className="text-xl md:text-2xl font-semibold text-center mb-10 text-accent">{t("aboutUs.purposeSubtitle")}</h2>
          <p className="text-muted-foreground text-lg text-center mb-10 max-w-3xl mx-auto">{t("aboutUs.purposeDesc")}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16">
            {purposeItems.map((item, i) => (
              <div key={i} className="text-center transition-transform duration-300 hover:-translate-y-2">
                <h3 className="font-bold text-lg mb-3">{item.title}</h3>
                <p className="text-base text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
          <img src="/images/pages/about-venn.png" alt="Explore, Enlighten, Growth - Venn diagram" className="mx-auto max-w-xl w-full" width={600} height={500} loading="lazy" />
        </div>
      </AnimatedSection>

      <AnimatedSection className="bg-card py-20 md:py-32">
        <div className="container max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-heading mb-8">{t("aboutUs.whyTitle")}</h2>
          <Button asChild variant="outline" className="rounded-full font-bold border-foreground mb-10 transition-transform duration-200 hover:scale-105">
            <Link to={`${prefix}/story-of-queebi-land`} onClick={() => trackCTAClick('story_link')}>{t("aboutUs.storyLink")}</Link>
          </Button>
          <img src="/images/pages/about-animals.gif" alt="Queebi characters animated" className="mx-auto max-w-lg w-full rounded-xl" width={500} height={400} loading="lazy" />
        </div>
      </AnimatedSection>

      <AnimatedSection className="bg-cover bg-center py-20 md:py-32" style={{ backgroundImage: `url(/images/brand/bg-pattern.png)` }}>
        <div className="container max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-heading mb-8">{t("aboutUs.whoTitle")}</h2>
          <p className="text-muted-foreground text-lg mb-4">{t("aboutUs.whoDesc")}</p>
          <p className="text-xl font-semibold">{t("aboutUs.whoBelief")}</p>
        </div>
      </AnimatedSection>

      <AnimatedSection className="py-20 md:py-32 bg-queebi-teal/30">
        <div className="container max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-center mb-6">{t("aboutUs.approachTitle")}</h2>
          <p className="text-muted-foreground text-center text-lg mb-14 max-w-3xl mx-auto">{t("aboutUs.approachDesc")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((p, i) => (
              <div key={i} className="bg-card rounded-xl p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <h3 className="font-bold text-lg mb-3">{p.title}</h3>
                <p className="text-base text-muted-foreground">{p.desc}</p>
              </div>
            ))}
          </div>
          <img src="/images/pages/about-classroom.png" alt="Teacher using Queebi activities with children" className="mt-14 w-full rounded-2xl" width={800} height={400} loading="lazy" />
        </div>
      </AnimatedSection>

      <AnimatedSection className="bg-background py-20 md:py-32">
        <div className="container max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-heading mb-8">{t("aboutUs.visionTitle")}</h2>
          <p className="text-muted-foreground text-lg mb-4">{t("aboutUs.visionP1")}</p>
          <p className="text-muted-foreground text-lg">{t("aboutUs.visionP2")}</p>
        </div>
      </AnimatedSection>

      <AnimatedSection className="bg-card py-20 md:py-32">
        <div className="container max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-center mb-14">{t("aboutUs.faqTitle")}</h2>
          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-background rounded-xl p-8 transition-all duration-300 hover:shadow-md">
                <h3 className="font-bold text-lg mb-3">{faq.q}</h3>
                <p className="text-base text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            }),
          }}
        />
      </AnimatedSection>
    </>
  );
};

export default AboutUs;
